<script lang="ts">
  import type {
    CompetitorServicePresence,
    ContractStatusFilter,
    FirmListQuery,
    LastContactBucket,
    LegalTrainingTermScope,
    NextActionBucket,
    PhonePresence,
  } from "@shared/types/firm";
  import {
    BAR_ASSOCIATION_DISPLAY_OPTIONS,
    CLIENT_RATIO_CATEGORY_OPTIONS,
    CONTRACT_FILTER_OPTIONS,
    CUSTOMER_STATUS_OPTIONS,
    CUSTOMER_STATUS_VALUES,
    LAST_CONTACT_BUCKET_OPTIONS,
    LAWYER_COUNT_BUCKET_OPTIONS,
    NEXT_ACTION_BUCKET_OPTIONS,
    ORGANIZATION_TYPE_OPTIONS,
    LEVEL_LMH_OPTIONS,
    FIRM_PRACTICE_AREA_OPTIONS,
    type SimpleOption,
  } from "@shared/constants/choices";
  import {
    ACTION_TYPE_LABELS,
    ACTION_TYPE_ORDER,
  } from "@shared/types/sales-action";
  import { readStringArray } from "@shared/lib/list-view-state";

  interface Props {
    open: boolean;
    query: FirmListQuery;
    /** 担当者候補 (invited/active)。 ページ側で app_accounts から取得して渡す。 */
    accountOptions: SimpleOption[];
    /** 詳細画面で登録できる外部ソリューションマスタ。 */
    competitorServiceOptions: SimpleOption[];
    /** 現在の一覧件数 (draft のライブ件数ではない)。 */
    total: number;
    onClose: () => void;
    onApply: (query: FirmListQuery) => void;
    onClear: () => void;
  }

  let {
    open,
    query,
    accountOptions,
    competitorServiceOptions,
    total,
    onClose,
    onApply,
    onClear,
  }: Props = $props();

  let draft = $state<FirmListQuery>({});
  // F5: 市区町村は生文字列を state に保持し、 blur / 適用時にだけ配列へ parse する。
  // oninput で正規化するとカンマ入力が即座に消えるため、 生文字列のまま持つ。
  let cityInput = $state<string>("");
  let closeButtonEl = $state<HTMLButtonElement | null>(null);

  const UNASSIGNED = "__unassigned__";

  const PHONE_PRESENCE_OPTIONS: SimpleOption[] = [
    { value: "present", label: "電話番号あり" },
    { value: "missing", label: "電話番号なし" },
  ];

  const IT_LITERACY_OPTIONS: SimpleOption[] = [
    ...LEVEL_LMH_OPTIONS,
    { value: "unset", label: "未設定" },
  ];

  const COMPETITOR_SERVICE_PRESENCE_OPTIONS: SimpleOption[] = [
    { value: "present", label: "登録あり" },
    { value: "missing", label: "登録なし" },
  ];

  const TERM_SCOPE_OPTIONS: SimpleOption[] = [
    { value: "any", label: "所属弁護士のいずれかが該当" },
    { value: "representative", label: "代表弁護士の期" },
    { value: "oldest", label: "最も古い期の弁護士" },
  ];

  const ACTION_TYPE_OPTIONS: SimpleOption[] = ACTION_TYPE_ORDER.map(
    (value) => ({
      value,
      label: ACTION_TYPE_LABELS[value],
    }),
  );

  type MultiKey =
    | "bar_associations"
    | "organization_types"
    | "lawyer_count_buckets"
    | "practice_areas"
    | "client_ratio_categories"
    | "it_literacies"
    | "competitor_service_ids"
    | "next_action_types";

  $effect(() => {
    if (open) {
      const next = cloneQuery(query);
      draft = next;
      cityInput = (next.cities ?? []).join("、");
    }
  });

  // F10: open した瞬間に閉じるボタンへ focus を移す。
  $effect(() => {
    if (open) closeButtonEl?.focus();
  });

  function cloneQuery(value: FirmListQuery): FirmListQuery {
    return {
      ...value,
      cities: value.cities ? [...value.cities] : undefined,
      lawyer_count_buckets: value.lawyer_count_buckets
        ? [...value.lawyer_count_buckets]
        : undefined,
      bar_associations: value.bar_associations
        ? [...value.bar_associations]
        : undefined,
      prefectures: value.prefectures ? [...value.prefectures] : undefined,
      organization_types: value.organization_types
        ? [...value.organization_types]
        : undefined,
      practice_areas: value.practice_areas
        ? [...value.practice_areas]
        : undefined,
      client_ratio_categories: value.client_ratio_categories
        ? [...value.client_ratio_categories]
        : undefined,
      it_literacies: value.it_literacies ? [...value.it_literacies] : undefined,
      competitor_service_ids: value.competitor_service_ids
        ? [...value.competitor_service_ids]
        : undefined,
      product_ids: value.product_ids ? [...value.product_ids] : undefined,
      assignee_account_ids: value.assignee_account_ids
        ? [...value.assignee_account_ids]
        : undefined,
      top_sales_grades: value.top_sales_grades
        ? [...value.top_sales_grades]
        : undefined,
      next_action_types: value.next_action_types
        ? [...value.next_action_types]
        : undefined,
    };
  }

  function selectValue(ev: Event): string {
    return (ev.currentTarget as HTMLSelectElement).value;
  }

  function inputValue(ev: Event): string {
    return (ev.currentTarget as HTMLInputElement).value;
  }

  // F5: `,` `、` 改行区切り + trim + 空除去。
  function splitValues(value: string): string[] {
    return value
      .split(/[,\n、]/)
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }

  function labelFromOptions(options: SimpleOption[], value: string): string {
    return options.find((option) => option.value === value)?.label ?? value;
  }

  // ── 市区町村 (raw string state) ──────────────────────────────────────────
  function commitCities(): void {
    const values = splitValues(cityInput);
    draft = {
      ...draft,
      cities: values.length > 0 ? values : undefined,
    };
  }

  // ── multi 値フィルタ (select で追加 + チップで × 削除) ────────────────────
  function multiValues(key: MultiKey): string[] {
    return (draft[key] as string[] | undefined) ?? [];
  }

  function setMulti(key: MultiKey, values: string[]): void {
    draft = {
      ...draft,
      [key]: values.length > 0 ? values : undefined,
    } as FirmListQuery;
  }

  function addMulti(key: MultiKey, value: string): void {
    if (!value) return;
    const current = multiValues(key);
    if (current.includes(value)) return;
    setMulti(key, [...current, value]);
  }

  function removeMulti(key: MultiKey, value: string): void {
    setMulti(
      key,
      multiValues(key).filter((item) => item !== value),
    );
  }

  function onAddSelect(key: MultiKey, ev: Event): void {
    const select = ev.currentTarget as HTMLSelectElement;
    addMulti(key, select.value);
    select.value = "";
  }

  function addCompetitorService(ev: Event): void {
    const select = ev.currentTarget as HTMLSelectElement;
    const value = select.value;
    select.value = "";
    if (!value || multiValues("competitor_service_ids").includes(value)) return;
    draft = {
      ...draft,
      competitor_service_ids: [...multiValues("competitor_service_ids"), value],
      competitor_service_presence:
        draft.competitor_service_presence === "missing"
          ? undefined
          : draft.competitor_service_presence,
    };
  }

  // ── ステータス (全9値。 全選択 / 未選択は「既定 = 絞り込みなし」) ──────
  const statusSelection = $derived<string[]>(
    draft.top_sales_grades &&
      draft.top_sales_grades.length < CUSTOMER_STATUS_VALUES.length
      ? draft.top_sales_grades
      : [],
  );

  function setStatusSelection(values: string[]): void {
    draft = {
      ...draft,
      top_sales_grades:
        values.length === 0 || values.length >= CUSTOMER_STATUS_VALUES.length
          ? undefined
          : values,
    };
  }

  function addStatus(ev: Event): void {
    const select = ev.currentTarget as HTMLSelectElement;
    const value = select.value;
    select.value = "";
    if (!value || statusSelection.includes(value)) return;
    setStatusSelection([...statusSelection, value]);
  }

  function removeStatus(value: string): void {
    setStatusSelection(statusSelection.filter((item) => item !== value));
  }

  // ── 担当者 (未担当 と 担当者複数選択 の相互排他) ──────────────────────────
  const assigneeIds = $derived<string[]>(draft.assignee_account_ids ?? []);
  const unassignedSelected = $derived(draft.unassigned_only === true);

  function addAssignee(ev: Event): void {
    const select = ev.currentTarget as HTMLSelectElement;
    const value = select.value;
    select.value = "";
    if (!value) return;
    if (value === UNASSIGNED) {
      draft = {
        ...draft,
        unassigned_only: true,
        assignee_account_ids: undefined,
      };
      return;
    }
    if (assigneeIds.includes(value)) return;
    draft = {
      ...draft,
      unassigned_only: undefined,
      assignee_account_ids: [...assigneeIds, value],
    };
  }

  function removeUnassigned(): void {
    draft = { ...draft, unassigned_only: undefined };
  }

  function removeAssignee(value: string): void {
    const next = assigneeIds.filter((item) => item !== value);
    draft = {
      ...draft,
      assignee_account_ids: next.length > 0 ? next : undefined,
    };
  }

  // ── 単一選択フィルタ ──────────────────────────────────────────────────────
  function setPhonePresence(value: string): void {
    draft = {
      ...draft,
      phone_presence: value ? (value as PhonePresence) : undefined,
    };
  }

  function setCompetitorServicePresence(value: string): void {
    const presence = value ? (value as CompetitorServicePresence) : undefined;
    draft = {
      ...draft,
      competitor_service_presence: presence,
      competitor_service_ids:
        presence === "missing" ? undefined : draft.competitor_service_ids,
    };
  }

  function setBooleanFilter(
    key: "bulk_sales_all_excluded" | "is_competitor",
    value: string,
  ): void {
    draft = {
      ...draft,
      [key]: value === "" ? undefined : value === "true",
    };
  }

  function setLastContact(value: string): void {
    draft = {
      ...draft,
      last_contact_bucket: value ? (value as LastContactBucket) : undefined,
    };
  }

  function setNextActionBucket(value: string): void {
    draft = {
      ...draft,
      next_action_bucket: value ? (value as NextActionBucket) : undefined,
    };
  }

  function setTermScope(value: string): void {
    draft = {
      ...draft,
      legal_training_term_scope: value
        ? (value as LegalTrainingTermScope)
        : undefined,
    };
  }

  function setTermBound(
    key: "legal_training_term_from" | "legal_training_term_to",
    value: string,
  ): void {
    const trimmed = value.trim();
    const parsed = trimmed.length > 0 ? Number(trimmed) : undefined;
    draft = {
      ...draft,
      [key]:
        parsed && Number.isFinite(parsed) && parsed > 0
          ? Math.trunc(parsed)
          : undefined,
    };
  }

  function setContractStatus(value: string): void {
    draft = {
      ...draft,
      contract_status_filter: value
        ? (value as ContractStatusFilter)
        : undefined,
    };
  }

  // F9: 期の下限 > 上限 はドロワー側でも弾く (backend の 400 と二重防御)。
  const termRangeInvalid = $derived(
    draft.legal_training_term_from != null &&
      draft.legal_training_term_to != null &&
      draft.legal_training_term_from > draft.legal_training_term_to,
  );

  function apply(): void {
    if (termRangeInvalid) return;
    // blur を挟まず適用した場合に備え、 生文字列をここで parse する (F5)。
    // 復元時 (validator の readStringArray) と同じ入力キャップ (dedup / 120 文字超破棄 /
    // 先頭 100 件) を適用時にも通し、 適用集合と復元集合を常に一致させる。
    const cities = readStringArray(splitValues(cityInput)) ?? [];
    onApply({
      ...draft,
      cities: cities.length > 0 ? cities : undefined,
      page: 1,
    });
    onClose();
  }

  function clearAndClose(): void {
    onClear();
    onClose();
  }

  function onKey(ev: KeyboardEvent): void {
    if (open && ev.key === "Escape") onClose();
  }
</script>

<svelte:window onkeydown={onKey} />

{#if open}
  <div class="drawer-root">
    <button class="backdrop" type="button" aria-label="閉じる" onclick={onClose}
    ></button>
    <aside
      class="drawer"
      role="dialog"
      aria-modal="true"
      aria-labelledby="advanced-filter-title"
    >
      <header class="drawer-head">
        <div>
          <h2 id="advanced-filter-title">詳細条件で絞り込み</h2>
          <p>現在の一覧: {total.toLocaleString()}件</p>
        </div>
        <button
          type="button"
          class="icon-button"
          aria-label="閉じる"
          bind:this={closeButtonEl}
          onclick={onClose}>×</button
        >
      </header>

      <div class="drawer-body">
        <section class="filter-section">
          <h3>基本情報</h3>
          <div class="field">
            <span>弁護士会</span>
            <div class="multi-control">
              {#if multiValues("bar_associations").length > 0}
                <ul class="chips">
                  {#each multiValues("bar_associations") as value (value)}
                    <li class="chip">
                      <span
                        >{labelFromOptions(
                          BAR_ASSOCIATION_DISPLAY_OPTIONS,
                          value,
                        )}</span
                      >
                      <button
                        type="button"
                        aria-label={`弁護士会: ${labelFromOptions(BAR_ASSOCIATION_DISPLAY_OPTIONS, value)} を外す`}
                        onclick={() => removeMulti("bar_associations", value)}
                        >×</button
                      >
                    </li>
                  {/each}
                </ul>
              {/if}
              <select
                data-testid="advanced-filter-bar-add"
                aria-label="弁護士会を追加"
                onchange={(ev) => onAddSelect("bar_associations", ev)}
              >
                <option value="">追加…</option>
                {#each BAR_ASSOCIATION_DISPLAY_OPTIONS as option (option.value)}
                  <option
                    value={option.value}
                    disabled={multiValues("bar_associations").includes(
                      option.value,
                    )}>{option.label}</option
                  >
                {/each}
              </select>
            </div>
          </div>
          <div class="field">
            <span>担当者</span>
            <div class="multi-control">
              {#if unassignedSelected || assigneeIds.length > 0}
                <ul class="chips">
                  {#if unassignedSelected}
                    <li class="chip">
                      <span>未担当</span>
                      <button
                        type="button"
                        aria-label="担当者: 未担当 を外す"
                        onclick={removeUnassigned}>×</button
                      >
                    </li>
                  {/if}
                  {#each assigneeIds as value (value)}
                    <li class="chip">
                      <span>{labelFromOptions(accountOptions, value)}</span>
                      <button
                        type="button"
                        aria-label={`担当者: ${labelFromOptions(accountOptions, value)} を外す`}
                        onclick={() => removeAssignee(value)}>×</button
                      >
                    </li>
                  {/each}
                </ul>
              {/if}
              <select
                data-testid="advanced-filter-assignee-add"
                aria-label="担当者を追加"
                onchange={addAssignee}
              >
                <option value="">追加…</option>
                <option value={UNASSIGNED} disabled={unassignedSelected}
                  >未担当</option
                >
                {#each accountOptions as option (option.value)}
                  <option
                    value={option.value}
                    disabled={assigneeIds.includes(option.value)}
                    >{option.label}</option
                  >
                {/each}
              </select>
            </div>
          </div>
          <div class="field">
            <span>組織形態</span>
            <div class="multi-control">
              {#if multiValues("organization_types").length > 0}
                <ul class="chips">
                  {#each multiValues("organization_types") as value (value)}
                    <li class="chip">
                      <span
                        >{labelFromOptions(
                          ORGANIZATION_TYPE_OPTIONS,
                          value,
                        )}</span
                      >
                      <button
                        type="button"
                        aria-label={`組織形態: ${labelFromOptions(ORGANIZATION_TYPE_OPTIONS, value)} を外す`}
                        onclick={() => removeMulti("organization_types", value)}
                        >×</button
                      >
                    </li>
                  {/each}
                </ul>
              {/if}
              <select
                data-testid="advanced-filter-organization-add"
                aria-label="組織形態を追加"
                onchange={(ev) => onAddSelect("organization_types", ev)}
              >
                <option value="">追加…</option>
                {#each ORGANIZATION_TYPE_OPTIONS as option (option.value)}
                  <option
                    value={option.value}
                    disabled={multiValues("organization_types").includes(
                      option.value,
                    )}>{option.label}</option
                  >
                {/each}
              </select>
            </div>
          </div>
        </section>

        <section class="filter-section">
          <h3>連絡先</h3>
          <label>
            <span>市区町村</span>
            <input
              data-testid="advanced-filter-cities"
              value={cityInput}
              placeholder="千代田区、港区"
              oninput={(ev) => (cityInput = inputValue(ev))}
              onblur={commitCities}
            />
          </label>
          <label>
            <span>電話番号</span>
            <select
              value={draft.phone_presence ?? ""}
              onchange={(ev) => setPhonePresence(selectValue(ev))}
            >
              <option value="">指定なし</option>
              {#each PHONE_PRESENCE_OPTIONS as option (option.value)}
                <option value={option.value}>{option.label}</option>
              {/each}
            </select>
          </label>
        </section>

        <section class="filter-section">
          <h3>属性</h3>
          <div class="field">
            <span>主要取扱分野</span>
            <div class="multi-control">
              {#if multiValues("practice_areas").length > 0}
                <ul class="chips">
                  {#each multiValues("practice_areas") as value (value)}
                    <li class="chip">
                      <span
                        >{labelFromOptions(FIRM_PRACTICE_AREA_OPTIONS, value)}</span
                      >
                      <button
                        type="button"
                        aria-label={`主要取扱分野: ${labelFromOptions(FIRM_PRACTICE_AREA_OPTIONS, value)} を外す`}
                        onclick={() => removeMulti("practice_areas", value)}
                        >×</button
                      >
                    </li>
                  {/each}
                </ul>
              {/if}
              <select
                data-testid="advanced-filter-practice-area-add"
                aria-label="主要取扱分野を追加"
                onchange={(ev) => onAddSelect("practice_areas", ev)}
              >
                <option value="">追加…</option>
                {#each FIRM_PRACTICE_AREA_OPTIONS as option (option.value)}
                  <option
                    value={option.value}
                    disabled={multiValues("practice_areas").includes(
                      option.value,
                    )}>{option.label}</option
                  >
                {/each}
              </select>
            </div>
          </div>
          <div class="field">
            <span>クライアント比率</span>
            <div class="multi-control">
              {#if multiValues("client_ratio_categories").length > 0}
                <ul class="chips">
                  {#each multiValues("client_ratio_categories") as value (value)}
                    <li class="chip">
                      <span
                        >{labelFromOptions(
                          CLIENT_RATIO_CATEGORY_OPTIONS,
                          value,
                        )}</span
                      >
                      <button
                        type="button"
                        aria-label={`クライアント比率: ${labelFromOptions(CLIENT_RATIO_CATEGORY_OPTIONS, value)} を外す`}
                        onclick={() =>
                          removeMulti("client_ratio_categories", value)}
                        >×</button
                      >
                    </li>
                  {/each}
                </ul>
              {/if}
              <select
                data-testid="advanced-filter-client-ratio-add"
                aria-label="クライアント比率を追加"
                onchange={(ev) => onAddSelect("client_ratio_categories", ev)}
              >
                <option value="">追加…</option>
                {#each CLIENT_RATIO_CATEGORY_OPTIONS as option (option.value)}
                  <option
                    value={option.value}
                    disabled={multiValues("client_ratio_categories").includes(
                      option.value,
                    )}>{option.label}</option
                  >
                {/each}
              </select>
            </div>
          </div>
          <div class="field">
            <span>ITリテラシー</span>
            <div class="multi-control">
              {#if multiValues("it_literacies").length > 0}
                <ul class="chips">
                  {#each multiValues("it_literacies") as value (value)}
                    <li class="chip">
                      <span>{labelFromOptions(IT_LITERACY_OPTIONS, value)}</span
                      >
                      <button
                        type="button"
                        aria-label={`ITリテラシー: ${labelFromOptions(IT_LITERACY_OPTIONS, value)} を外す`}
                        onclick={() => removeMulti("it_literacies", value)}
                        >×</button
                      >
                    </li>
                  {/each}
                </ul>
              {/if}
              <select
                data-testid="advanced-filter-it-literacy-add"
                aria-label="ITリテラシーを追加"
                onchange={(ev) => onAddSelect("it_literacies", ev)}
              >
                <option value="">追加…</option>
                {#each IT_LITERACY_OPTIONS as option (option.value)}
                  <option
                    value={option.value}
                    disabled={multiValues("it_literacies").includes(
                      option.value,
                    )}>{option.label}</option
                  >
                {/each}
              </select>
            </div>
          </div>
        </section>

        <section class="filter-section">
          <h3>利用ソリューション</h3>
          <label>
            <span>登録状況</span>
            <select
              data-testid="advanced-filter-competitor-service-presence"
              value={draft.competitor_service_presence ?? ""}
              onchange={(ev) => setCompetitorServicePresence(selectValue(ev))}
            >
              <option value="">指定なし</option>
              {#each COMPETITOR_SERVICE_PRESENCE_OPTIONS as option (option.value)}
                <option value={option.value}>{option.label}</option>
              {/each}
            </select>
          </label>
          <div class="field">
            <span>登録済み利用ソリューション</span>
            <div class="multi-control">
              {#if multiValues("competitor_service_ids").length > 0}
                <ul class="chips">
                  {#each multiValues("competitor_service_ids") as value (value)}
                    <li class="chip">
                      <span
                        >{labelFromOptions(
                          competitorServiceOptions,
                          value,
                        )}</span
                      >
                      <button
                        type="button"
                        aria-label={`登録済み利用ソリューション: ${labelFromOptions(competitorServiceOptions, value)} を外す`}
                        onclick={() =>
                          removeMulti("competitor_service_ids", value)}
                        >×</button
                      >
                    </li>
                  {/each}
                </ul>
              {/if}
              <select
                data-testid="advanced-filter-competitor-service-add"
                aria-label="登録済み利用ソリューションを追加"
                disabled={draft.competitor_service_presence === "missing"}
                onchange={addCompetitorService}
              >
                <option value="">追加…</option>
                {#each competitorServiceOptions as option (option.value)}
                  <option
                    value={option.value}
                    disabled={multiValues("competitor_service_ids").includes(
                      option.value,
                    )}>{option.label}</option
                  >
                {/each}
              </select>
            </div>
          </div>
        </section>

        <section class="filter-section">
          <h3>営業対象外設定</h3>
          <label>
            <span>一括営業</span>
            <select
              data-testid="advanced-filter-bulk-sales-all-excluded"
              value={draft.bulk_sales_all_excluded == null
                ? ""
                : String(draft.bulk_sales_all_excluded)}
              onchange={(ev) =>
                setBooleanFilter("bulk_sales_all_excluded", selectValue(ev))}
            >
              <option value="">指定なし</option>
              <option value="true">すべて対象外</option>
              <option value="false">すべて対象外ではない</option>
            </select>
          </label>
          <label>
            <span>競合</span>
            <select
              data-testid="advanced-filter-competitor"
              value={draft.is_competitor == null
                ? ""
                : String(draft.is_competitor)}
              onchange={(ev) =>
                setBooleanFilter("is_competitor", selectValue(ev))}
            >
              <option value="">指定なし</option>
              <option value="true">競合</option>
              <option value="false">競合ではない</option>
            </select>
          </label>
        </section>

        <section class="filter-section">
          <h3>所属弁護士</h3>
          <label>
            <span>期の判定対象</span>
            <select
              value={draft.legal_training_term_scope ?? "any"}
              onchange={(ev) => setTermScope(selectValue(ev))}
            >
              {#each TERM_SCOPE_OPTIONS as option (option.value)}
                <option value={option.value}>{option.label}</option>
              {/each}
            </select>
          </label>
          <div class="inline-fields">
            <label>
              <span>期 下限</span>
              <input
                type="number"
                min="1"
                data-testid="advanced-filter-term-from"
                value={draft.legal_training_term_from ?? ""}
                oninput={(ev) =>
                  setTermBound("legal_training_term_from", inputValue(ev))}
              />
            </label>
            <label>
              <span>期 上限</span>
              <input
                type="number"
                min="1"
                data-testid="advanced-filter-term-to"
                value={draft.legal_training_term_to ?? ""}
                oninput={(ev) =>
                  setTermBound("legal_training_term_to", inputValue(ev))}
              />
            </label>
          </div>
          {#if termRangeInvalid}
            <p class="field-error" data-testid="advanced-filter-term-error">
              期の下限は上限以下にしてください
            </p>
          {/if}
          <div class="field">
            <span>所属弁護士数</span>
            <div class="multi-control">
              {#if multiValues("lawyer_count_buckets").length > 0}
                <ul class="chips">
                  {#each multiValues("lawyer_count_buckets") as value (value)}
                    <li class="chip">
                      <span
                        >{labelFromOptions(
                          LAWYER_COUNT_BUCKET_OPTIONS,
                          value,
                        )}</span
                      >
                      <button
                        type="button"
                        aria-label={`所属弁護士数: ${labelFromOptions(LAWYER_COUNT_BUCKET_OPTIONS, value)} を外す`}
                        onclick={() =>
                          removeMulti("lawyer_count_buckets", value)}>×</button
                      >
                    </li>
                  {/each}
                </ul>
              {/if}
              <select
                data-testid="advanced-filter-lawyer-count-add"
                aria-label="所属弁護士数を追加"
                onchange={(ev) => onAddSelect("lawyer_count_buckets", ev)}
              >
                <option value="">追加…</option>
                {#each LAWYER_COUNT_BUCKET_OPTIONS as option (option.value)}
                  <option
                    value={option.value}
                    disabled={multiValues("lawyer_count_buckets").includes(
                      option.value,
                    )}>{option.label}</option
                  >
                {/each}
              </select>
            </div>
          </div>
        </section>

        <section class="filter-section">
          <h3>営業活動</h3>
          <div class="field">
            <span>ステータス</span>
            <div class="multi-control">
              {#if statusSelection.length > 0}
                <ul class="chips">
                  {#each statusSelection as value (value)}
                    <li class="chip">
                      <span
                        >{labelFromOptions(
                          CUSTOMER_STATUS_OPTIONS,
                          value,
                        )}</span
                      >
                      <button
                        type="button"
                        aria-label={`ステータス: ${labelFromOptions(CUSTOMER_STATUS_OPTIONS, value)} を外す`}
                        onclick={() => removeStatus(value)}>×</button
                      >
                    </li>
                  {/each}
                </ul>
              {/if}
              <select
                data-testid="advanced-filter-status-add"
                aria-label="ステータスを追加"
                onchange={addStatus}
              >
                <option value="">追加…</option>
                {#each CUSTOMER_STATUS_OPTIONS as option (option.value)}
                  <option
                    value={option.value}
                    disabled={statusSelection.includes(option.value)}
                    >{option.label}</option
                  >
                {/each}
              </select>
            </div>
          </div>
          <label>
            <span>最終接触</span>
            <select
              value={draft.last_contact_bucket ?? ""}
              onchange={(ev) => setLastContact(selectValue(ev))}
            >
              <option value="">指定なし</option>
              {#each LAST_CONTACT_BUCKET_OPTIONS as option (option.value)}
                <option value={option.value}>{option.label}</option>
              {/each}
            </select>
          </label>
          <label>
            <span>次アクション期限</span>
            <select
              value={draft.next_action_bucket ?? ""}
              onchange={(ev) => setNextActionBucket(selectValue(ev))}
            >
              <option value="">指定なし</option>
              {#each NEXT_ACTION_BUCKET_OPTIONS as option (option.value)}
                <option value={option.value}>{option.label}</option>
              {/each}
            </select>
          </label>
          <div class="field">
            <span>次アクション種別</span>
            <div class="multi-control">
              {#if multiValues("next_action_types").length > 0}
                <ul class="chips">
                  {#each multiValues("next_action_types") as value (value)}
                    <li class="chip">
                      <span>{labelFromOptions(ACTION_TYPE_OPTIONS, value)}</span
                      >
                      <button
                        type="button"
                        aria-label={`次アクション種別: ${labelFromOptions(ACTION_TYPE_OPTIONS, value)} を外す`}
                        onclick={() => removeMulti("next_action_types", value)}
                        >×</button
                      >
                    </li>
                  {/each}
                </ul>
              {/if}
              <select
                data-testid="advanced-filter-next-action-add"
                aria-label="次アクション種別を追加"
                onchange={(ev) => onAddSelect("next_action_types", ev)}
              >
                <option value="">追加…</option>
                {#each ACTION_TYPE_OPTIONS as option (option.value)}
                  <option
                    value={option.value}
                    disabled={multiValues("next_action_types").includes(
                      option.value,
                    )}>{option.label}</option
                  >
                {/each}
              </select>
            </div>
          </div>
        </section>

        <section class="filter-section">
          <h3>契約</h3>
          <label>
            <span>契約ステータス</span>
            <select
              value={draft.contract_status_filter ?? ""}
              onchange={(ev) => setContractStatus(selectValue(ev))}
            >
              <option value="">指定なし</option>
              {#each CONTRACT_FILTER_OPTIONS as option (option.value)}
                <option value={option.value}>{option.label}</option>
              {/each}
            </select>
          </label>
        </section>
      </div>

      <footer class="drawer-foot">
        <button
          type="button"
          class="secondary"
          data-testid="advanced-filter-clear"
          onclick={clearAndClose}>条件をクリア</button
        >
        <div class="spacer"></div>
        <button type="button" class="secondary" onclick={onClose}
          >キャンセル</button
        >
        <button
          type="button"
          class="primary"
          data-testid="advanced-filter-apply"
          disabled={termRangeInvalid}
          onclick={apply}>適用</button
        >
      </footer>
    </aside>
  </div>
{/if}

<style>
  .drawer-root {
    position: fixed;
    inset: 0;
    z-index: 50;
  }
  .backdrop {
    position: absolute;
    inset: 0;
    border: 0;
    background: rgba(15, 23, 42, 0.28);
    cursor: default;
  }
  .drawer {
    position: absolute;
    top: 0;
    right: 0;
    width: min(440px, 100vw);
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #ffffff;
    box-shadow: -18px 0 40px rgba(15, 23, 42, 0.18);
    animation: slide-in 140ms ease-out;
  }
  .drawer-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 22px 14px;
    border-bottom: 1px solid #e5e7eb;
  }
  .drawer-head h2 {
    margin: 0;
    font-size: 1.0625rem;
  }
  .drawer-head p {
    margin: 6px 0 0;
    font-size: 0.8125rem;
    color: #6b7280;
  }
  .icon-button {
    width: 32px;
    height: 32px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: #ffffff;
    color: #374151;
    cursor: pointer;
    font-size: 20px;
    line-height: 1;
  }
  .drawer-body {
    flex: 1;
    overflow: auto;
    padding: 18px 22px 96px;
  }
  .filter-section {
    padding: 0 0 18px;
    margin: 0 0 18px;
    border-bottom: 1px solid #eef2f7;
  }
  .filter-section:last-child {
    border-bottom: 0;
  }
  .filter-section h3 {
    margin: 0 0 12px;
    font-size: 0.875rem;
    color: #111827;
  }
  label,
  .field {
    display: grid;
    grid-template-columns: 112px minmax(0, 1fr);
    align-items: center;
    gap: 12px;
    margin: 10px 0;
  }
  .field {
    align-items: start;
  }
  label span,
  .field > span {
    font-size: 0.8125rem;
    font-weight: 700;
    color: #4b5563;
  }
  .field > span {
    padding-top: 8px;
  }
  select,
  input {
    min-width: 0;
    width: 100%;
    height: 36px;
    padding: 6px 10px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: #ffffff;
    color: #111827;
    font: inherit;
    font-size: 0.875rem;
  }
  select:focus,
  input:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.14);
  }
  .multi-control {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 4px 2px 10px;
    border-radius: 999px;
    background: #eef2ff;
    color: #3730a3;
    font-size: 0.75rem;
  }
  .chip button {
    width: auto;
    height: auto;
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
    font: inherit;
    font-size: 0.9rem;
    line-height: 1;
    padding: 0 4px;
  }
  .field-error {
    grid-column: 1 / -1;
    margin: 2px 0 0;
    color: #dc2626;
    font-size: 0.75rem;
  }
  .inline-fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
  .inline-fields label {
    grid-template-columns: 1fr;
    gap: 5px;
    margin: 10px 0;
  }
  .drawer-foot {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 14px 22px;
    border-top: 1px solid #e5e7eb;
    background: #ffffff;
  }
  .spacer {
    flex: 1;
  }
  .drawer-foot button {
    padding: 8px 14px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    cursor: pointer;
    font: inherit;
    font-size: 0.875rem;
  }
  .drawer-foot .secondary {
    background: #ffffff;
    color: #374151;
  }
  .drawer-foot .secondary:hover {
    background: #f9fafb;
  }
  .drawer-foot .primary {
    background: #2563eb;
    border-color: #2563eb;
    color: #ffffff;
  }
  .drawer-foot .primary:hover:not(:disabled) {
    background: #1d4ed8;
  }
  .drawer-foot .primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  @keyframes slide-in {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }
  @media (max-width: 560px) {
    label,
    .field {
      grid-template-columns: 1fr;
      gap: 6px;
    }
    .field > span {
      padding-top: 0;
    }
  }
</style>
